import React, { useRef, useState, useLayoutEffect, memo, useMemo } from 'react';
import { blockerCardHeight, blockerStackLayout } from './blockerCardMetrics';

/**
 * PathwayArrow Component
 *
 * Renders a Bézier curve arrow connecting two nodes in the impact path.
 * Uses SVG path with cubic Bézier curves for smooth, organic connectors.
 *
 * Features:
 * - Smooth Bézier curve with configurable control points
 * - Arrowhead at the end point
 * - Hover effects
 * - Configurable stroke width and color
 *
 * @param {Object} props
 * @param {string} props.path - SVG path d attribute (Bézier curve)
 * @param {string} props.fromNodeId - Source node ID
 * @param {string} props.toNodeId - Target node ID
 * @param {string} props.stroke - Stroke color (default: #94a3b8)
 * @param {number} props.strokeWidth - Stroke width (default: 2)
 * @param {boolean} props.animated - Whether to animate the arrow (default: false)
 * @param {boolean} props.dashed - Whether to render as dashed line (default: false)
 * @param {Function} props.onClick - Click handler
 */
// Connector furniture, in SVG units: the (+) button, the knot, and the room
// each needs so the type pill never lands on top of either.
const PLUS_RADIUS = 22;
const KNOT_RADIUS = 8;
const KNOT_INSET = 34; // clearance between blocker card and the target node
const LABEL_GAP = 14;
/** How far before and after the stack the flow splits and comes back. */
const FAN_GAP = 52;  // breathing room between the pill and its neighbours
// A blocker card is exactly as wide as a node card: it sits in a node column
// and is the same kind of object. It grows taller, never wider.
const NODE_CARD_WIDTH = 360;

const CUT_RED = '#EF5350';
const MARKER_SIZE = 8;
export function markerIdFor(color) {
  return `pathway-arrowhead-${String(color || '#444').replace(/[^a-zA-Z0-9]/g, '')}`;
}
/** One arrowhead per colour in use, drawn once by the layer. */
function ArrowheadDefs({ colors }) {
  return (
    <defs>
      {colors.map((color) => (
        <marker
          key={color}
          id={markerIdFor(color)}
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth={MARKER_SIZE}
          markerHeight={MARKER_SIZE}
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
        </marker>
      ))}
    </defs>
  );
}

export default function PathwayArrow({
  path,
  fromNodeId,
  toNodeId,
  stroke = '#444',
  strokeWidth = 2,
  animated = false,
  dashed = false,
  onClick = null,
  // Impact Path v2: the edge's effect type, drawn on the path (Miro-style) and
  // tappable to change it. Absent for untyped edges — nothing is drawn.
  label = null,
  labelColor = null,
  // The label is the map's default assumption rather than a human choice —
  // drawn quieter so real decisions stand out against it.
  labelMuted = false,
  // Blockers pinned to this edge (specs/impact-path-v2-prd.md §4). The knot
  // sits just after the type pill, so a blocker is added BEFORE the
  // consequence — it is what stops this from reaching that.
  blockers = [],
  // Where the layout parked this edge's blocker card: its own column, between
  // the parent and the child it stops. Null on unblocked edges.
  blockerColumn = null,
  onKnotClick = null,
  labelAtEnd = false, // measure the pill near the line's end (route layout), not its middle
  avoids = false, // the edge leads into an unwanted consequence: its knot adds a way to avoid it (green)
  onLabelClick = null,
  // Selection: this edge is the one being looked at, or something else is.
  dimmed = false,
  onSelect = null,
  // "sama asia" runs both ways, so it carries no arrowhead.
  undirected = false,
}) {
  // Arrowheads are shared per colour (PathwayArrowLayer draws the defs
  // once): every arrow carrying its own <defs><marker> was three elements
  // times every edge on the map.
  const arrowId = markerIdFor(stroke);
  const cutMarkerId = markerIdFor(CUT_RED);

  // Put the label ON the curve rather than at a guessed offset: the connector is
  // a Bézier, so its midpoint is not the midpoint of the two node corners.
  // Measured from the rendered path, only for arrows that actually have a label.
  const pathRef = useRef(null);
  const [labelPoint, setLabelPoint] = useState(null);
  // Measured, not estimated: a guessed character width was 30% short for
  // "mahdollistaa", which put the pill on top of the (+).
  const textRef = useRef(null);
  const [labelWidth, setLabelWidth] = useState(label ? label.length * 7 + 24 : 0);

  // Only ever a point measured on this arrow's own drawn path.
  const lx = labelPoint?.x;
  const ly = labelPoint?.y;

  const blocked = blockers.length > 0;

  // The cards themselves are drawn by BlockerLayer, where the layout put them.
  // All this edge needs to know is how much of itself the stack covers, so the
  // connector can stop before it and the cut stub can carry on to the child.
  const blockerBox = {
    width: NODE_CARD_WIDTH,
    height: blockers.length ? blockerCardHeight(blockers, NODE_CARD_WIDTH) : 34,
  };


  // The knot rides the same curve a little further along than the pill.
  const [knotPoint, setKnotPoint] = useState(null);
  const [pathLengths, setPathLengths] = useState(null);
  // Where the connector splits before the stack and where it has to arrive
  // after it, both read off the real curve.
  const [fanPoints, setFanPoints] = useState(null);
  useLayoutEffect(() => {
    // Nothing to draw: no blocker on this edge, and the current mode does not
    // offer to add one. Clear rather than return — leaving a stale knotPoint
    // behind kept the knots on screen after a mode switch took them away.
    if (!onKnotClick && blockers.length === 0) {
      setKnotPoint(null);
      return;
    }
    if (!pathRef.current) return;
    try {
      const len = pathRef.current.getTotalLength();
      // A fixed distance before the target, so the knot stays put whatever the
      // edge's length: a blocker stops the flow just before it lands.
      // Sit the card so its far edge clears the target node, rather than at a
      // fixed inset that a wide card overruns.
      const halfCard = blocked ? blockerBox.width / 2 : KNOT_RADIUS;
      if (blockerColumn) {
        // Find where the curve reaches the stack, so the connector stops there
        // and the cut stub carries on to the target.
        let lo = 0;
        let hi = len;
        for (let i = 0; i < 24; i += 1) {
          const mid = (lo + hi) / 2;
          if (pathRef.current.getPointAtLength(mid).x < blockerColumn.x) lo = mid;
          else hi = mid;
        }
        const atStack = Math.max(0, lo - blockerBox.width / 2);
        const atFan = Math.max(0, atStack - FAN_GAP);
        // The knot stays a knot: a red dot on the connector before the stack,
        // and tapping it adds another blocker below the ones already there.
        const pt = pathRef.current.getPointAtLength(Math.max(0, atFan - KNOT_INSET / 2));
        setKnotPoint({ x: pt.x, y: pt.y });
        const fan = pathRef.current.getPointAtLength(atFan);
        const end = pathRef.current.getPointAtLength(len);
        setFanPoints({ fan: { x: fan.x, y: fan.y }, end: { x: end.x, y: end.y } });
        setPathLengths({ total: len, atKnot: atFan });
        return;
      }
      const at = Math.max(len * 0.5, len - (halfCard + KNOT_INSET));
      const pt = pathRef.current.getPointAtLength(at);
      setKnotPoint({ x: pt.x, y: pt.y });
      setFanPoints(null);
      setPathLengths({ total: len, atKnot: at });
    } catch {
      setKnotPoint(null);
    }
  }, [path, onKnotClick, blockers.length, blocked, blockerBox.width, blockerColumn?.x, blockerColumn?.y]);

  // Where the pill sits on the curve. Measured in distance along the path, not
  // as a fraction: a fraction ignores the pill's own width and lands it on the
  // (+) on short edges. With the blocker in its own column, the far end only
  // has to clear the knot.
  useLayoutEffect(() => {
    if (!label || !pathRef.current) return;
    try {
      const len = pathRef.current.getTotalLength();
      const clearOfPlus = PLUS_RADIUS + LABEL_GAP + labelWidth / 2;
      // On a blocked edge the connector is only drawn as far as the blocker
      // card, and the run past it is the red stub. Centring the pill on the
      // whole path put it behind the card; it belongs on the part you can see.
      const visibleRun = (blocked && pathLengths) ? pathLengths.atKnot : len;
      const clearOfEnd = blocked
        ? LABEL_GAP + labelWidth / 2
        : KNOT_INSET + KNOT_RADIUS * 2 + LABEL_GAP + labelWidth / 2;
      // The route layout's connectors share a vertical beside the parent; their
      // word belongs on the run into the child, so it is measured from the end.
      const end = Math.max(clearOfPlus, visibleRun - clearOfEnd);
      const at = labelAtEnd ? end : Math.min(Math.max(visibleRun * 0.5, clearOfPlus), end);
      const pt = pathRef.current.getPointAtLength(Math.min(at, len));
      setLabelPoint({ x: pt.x, y: pt.y });
    } catch (err) {
      // getTotalLength throws while the path is still detached; anything else
      // is a real bug and swallowing it left the pill at stale coordinates for
      // three renders before anyone noticed.
      if (!(err instanceof TypeError)) console.error('Arrow label placement failed:', err);
      setLabelPoint(null);
    }
  }, [label, path, labelWidth, blocked, pathLengths?.atKnot, labelAtEnd]);


  return (
    <g
      className="pathway-arrow"
      opacity={dimmed ? 0.09 : 1}
      style={{ transition: 'opacity 0.18s ease' }}
    >
      {/* Tapping the connector picks it out of the crowd. A wide invisible
          stroke, because a 2px line is not a target on a busy map. */}
      {onSelect && (
        <path
          d={path}
          stroke="transparent"
          strokeWidth={18}
          fill="none"
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            onSelect({
              key: `${fromNodeId}->${toNodeId}`,
              ids: new Set([fromNodeId, toNodeId]),
              from: fromNodeId,
              to: toNodeId,
              label,
              color: labelColor || stroke,
            });
          }}
        />
      )}
      {/* The flow splits at the obstacles and comes back together after them,
          the way a branch diagram shows one line becoming several and merging
          again. Drawn explicitly rather than as a gap in the connector: a line
          that simply stopped at the stack and reappeared past it read as
          passing straight through whichever card it happened to line up with. */}
      {blocked && blockerColumn && fanPoints && (() => {
        const stack = blockerStackLayout(blockers, blockerBox.width);
        const cardLeft = blockerColumn.x - blockerBox.width / 2;
        const cardRight = blockerColumn.x + blockerBox.width / 2;
        const joinX = cardRight + FAN_GAP;
        const joinY = blockerColumn.y;
        const branchColor = labelColor || stroke;
        const sIn = (cardLeft - fanPoints.fan.x) * 0.55;
        const sOut = (joinX - cardRight) * 0.55;
        return (
          <g className="pathway-arrow-fan">
            {stack.cards.map((card) => {
              const cy = blockerColumn.y + card.cy;
              return (
                <g key={card.blocker.id}>
                  <path
                    d={`M ${fanPoints.fan.x} ${fanPoints.fan.y} C ${fanPoints.fan.x + sIn} ${fanPoints.fan.y}, ${cardLeft - sIn} ${cy}, ${cardLeft} ${cy}`}
                    stroke={branchColor}
                    strokeWidth={strokeWidth}
                    fill="none"
                  />
                  <path
                    d={`M ${cardRight} ${cy} C ${cardRight + sOut} ${cy}, ${joinX - sOut} ${joinY}, ${joinX} ${joinY}`}
                    stroke="#EF5350"
                    strokeWidth={strokeWidth}
                    fill="none"
                  />
                </g>
              );
            })}
            <path
              d={`M ${joinX} ${joinY} C ${joinX + (fanPoints.end.x - joinX) * 0.4} ${joinY}, ${fanPoints.end.x - (fanPoints.end.x - joinX) * 0.4} ${fanPoints.end.y}, ${fanPoints.end.x} ${fanPoints.end.y}`}
              stroke="#EF5350"
              strokeWidth={strokeWidth}
              fill="none"
              markerEnd={`url(#${cutMarkerId})`}
            />
          </g>
        );
      })()}

      {/* Main arrow path */}
      <path
        ref={pathRef}
        d={path}
        stroke={blocked ? (labelColor || stroke) : stroke}
        strokeWidth={strokeWidth}
        fill="none"
        markerEnd={blocked || undirected ? undefined : `url(#${arrowId})`}
        style={{
          transition: 'stroke 0.3s ease, stroke-width 0.3s ease',
          cursor: onClick ? 'pointer' : 'default',
          ...(animated && {
            strokeDasharray: '5,5',
            animation: 'dash 1s linear infinite'
          }),
          ...(blocked && pathLengths
            ? { strokeDasharray: `${pathLengths.atKnot} ${pathLengths.total}` }
            : (dashed && !animated && {
              strokeDasharray: '8,4' // Dashed line for negative votes (no animation)
            }))
        }}
        onClick={onClick}
        onMouseEnter={(e) => {
          e.currentTarget.style.stroke = '#3b82f6';
          e.currentTarget.style.strokeWidth = strokeWidth + 1;
        }}
        // Clear, don't restore: an inline colour outranks the stroke attribute,
        // so writing this render's colour back would keep it after a highlight
        // has dimmed the edge (#980).
        onMouseLeave={(e) => {
          e.currentTarget.style.stroke = '';
          e.currentTarget.style.strokeWidth = '';
        }}
      />

      {/* Effect type on the path. Sized from the text length rather than measured:
          the labels are one short word and a sign, and a measured layout would
          cost a reflow per arrow on every pan. */}
      {label && lx != null && ly != null && (
        <g
          className="pathway-arrow-label"
          transform={`translate(${lx}, ${ly})`}
          style={{ cursor: onLabelClick ? 'pointer' : 'default' }}
          onClick={(e) => {
            if (!onLabelClick) return;
            e.stopPropagation();
            onLabelClick({ from: fromNodeId, to: toNodeId }, { x: e.clientX, y: e.clientY });
          }}
        >
          {/* Invisible hit area: the chip itself is ~22px tall in SVG units, which
              is a few pixels on a zoomed-out map — too small to tap. */}
          <rect
            x={-labelWidth / 2 - 14}
            y={-22}
            width={labelWidth + 28}
            height={44}
            rx={22}
            fill="transparent"
          />
          <rect
            x={-labelWidth / 2}
            y={-11}
            width={labelWidth}
            height={22}
            rx={11}
            /* Always opaque: fading the chip let the connector show straight
               through the word. Muting is done with colour, not transparency. */
            fill="#12121c"
            stroke={labelMuted ? 'rgba(255, 255, 255, 0.18)' : (labelColor || stroke)}
            strokeWidth={1}
          />
          <text
            ref={textRef}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={11}
            fontWeight={600}
            fill={labelMuted ? 'rgba(255, 255, 255, 0.45)' : (labelColor || 'rgba(255,255,255,0.75)')}
            style={{ userSelect: 'none', pointerEvents: 'none' }}
          >
            {label}
          </text>
        </g>
      )}

      {/* The knot: tap to pin a blocker on this edge. A red dot while it is an
          invitation, the blocker's own red card once something is on it. */}
      {knotPoint && (
        <g
          className={blocked ? 'pathway-arrow-knot blocked' : 'pathway-arrow-knot'}
          transform={`translate(${knotPoint.x}, ${knotPoint.y})`}
          style={{ cursor: onKnotClick ? 'pointer' : 'default' }}
          onClick={(e) => {
            if (!onKnotClick) return;
            e.stopPropagation();
            const box = e.currentTarget.getBoundingClientRect();
            onKnotClick(
              { from: fromNodeId, to: toNodeId, blockers },
              { x: box.left + box.width / 2, y: box.top + box.height / 2, centered: false },
            );
          }}
        >
          <circle r={20} fill="transparent" />
          {/* A solid dot, in the blocker colour rather than the connector's: it
              does not change with the edge type under it, nor with whether
              something is already there. Red where an obstacle to a wanted
              consequence goes; green before an unwanted one, where what goes in
              is a way to avoid it. */}
          <circle r={6} fill={avoids ? '#34D399' : '#EF5350'} />
        </g>
      )}

      {/* Invisible wider path for easier hover/click */}
      {onClick && (
        <path
          d={path}
          stroke="transparent"
          strokeWidth={strokeWidth * 5}
          fill="none"
          style={{ cursor: 'pointer' }}
          onClick={onClick}
        />
      )}

      <style>{`
        @keyframes dash {
          to {
            stroke-dashoffset: -10;
          }
        }
      `}</style>
    </g>
  );
}

/**
 * PathwayArrowLayer Component
 *
 * Renders a layer of all arrows for a pathway tree.
 * This should be rendered before the nodes so arrows appear behind them.
 *
 * @param {Object} props
 * @param {Array} props.arrows - Array of arrow objects {from, to, path}
 * @param {Object} props.arrowConfig - Arrow styling config
 */
// Memoised, and the config default is one shared object: the renderer
// re-renders on every zoom tick, and without this every arrow on the map —
// hundreds of them — reconciled each time for nothing.
const NO_CONFIG = {};
const MemoArrow = memo(PathwayArrow);

export const PathwayArrowLayer = memo(function PathwayArrowLayer({
  arrows = [], arrowConfig = NO_CONFIG, onLabelClick = null, onKnotClick = null,
  selection = null, onSelect = null,
  // Below reading zoom the type chips are unreadable anyway, and four
  // elements per edge is most of what makes a whole map heavy to move.
  showLabels = true,
}) {
  const colors = useMemo(() => {
    const set = new Set([CUT_RED]);
    arrows.forEach((a) => set.add(a.stroke || arrowConfig.stroke || '#444'));
    return [...set];
  }, [arrows, arrowConfig.stroke]);
  if (!arrows || arrows.length === 0) return null;

  return (
    <g className="pathway-arrows-layer">
      <ArrowheadDefs colors={colors} />
      {arrows.map((arrow, index) => (
        <MemoArrow
          key={`${arrow.from}-${arrow.to}-${index}`}
          path={arrow.path}
          fromNodeId={arrow.from}
          toNodeId={arrow.to}
          strokeWidth={arrow.strokeWidth} // Use arrow-specific strokeWidth
          dashed={arrow.dashed} // Dashed style for negative votes / secondary parents
          label={showLabels ? arrow.label : null}
          labelColor={arrow.labelColor}
          labelMuted={arrow.labelMuted}
          blockers={arrow.blockers}
          blockerColumn={arrow.blockerColumn}
          undirected={arrow.undirected}
          onKnotClick={onKnotClick}
          onSelect={onSelect}
          dimmed={Boolean(selection) && selection.key !== `${arrow.from}->${arrow.to}`}
          labelAtEnd={Boolean(arrow.labelAtEnd)}
          avoids={Boolean(arrow.avoids)}
          onLabelClick={onLabelClick}
          {...(arrow.stroke ? { stroke: arrow.stroke } : {})} // faded colour for borrowed/secondary edges
          {...arrowConfig}
        />
      ))}
    </g>
  );
});
